import { useForm } from "@refinedev/react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { PageHeader } from "@/components/forms/PageHeader";
import { ConfirmDialog } from "@/components/forms/ConfirmDialog";

export function FinancieraForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [pendingData, setPendingData] = useState<Record<string, unknown> | null>(null);

  const {
    refineCore: { onFinish, formLoading, queryResult },
    register,
    handleSubmit,
    watch,
  } = useForm({
    refineCoreProps: {
      resource: "financieras",
      action: isEdit ? "edit" : "create",
      id,
      redirect: false,
      onMutationSuccess: () => navigate("/admin/financieras"),
    },
  });

  const aplicaIva = watch("aplica_iva_fee");
  const aplica4x1000 = watch("aplica_4x1000");
  const fee = watch("fee_porcentaje");
  const original = queryResult?.data?.data;

  function onSubmit(data: Record<string, unknown>) {
    // Si edita y cambió alguna tarifa, pedir confirmación
    if (
      isEdit &&
      original &&
      (Number(data.fee_porcentaje) !== Number(original.fee_porcentaje) ||
        data.aplica_iva_fee !== original.aplica_iva_fee ||
        Number(data.porc_iva) !== Number(original.porc_iva) ||
        Number(data.porc_4x1000) !== Number(original.porc_4x1000))
    ) {
      setPendingData(data);
      return;
    }
    void onFinish(data);
  }

  return (
    <div className="max-w-2xl">
      <PageHeader title={isEdit ? "Editar financiera" : "Nueva financiera"} />
      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-4">
        <Field label="Código">
          <input {...register("codigo", { required: true })} className="input" placeholder="ADDI" />
        </Field>
        <Field label="Nombre">
          <input {...register("nombre", { required: true })} className="input" />
        </Field>
        <Field label="Fee (%)">
          <input
            type="number"
            step="0.1"
            {...register("fee_porcentaje", { valueAsNumber: true, required: true })}
            className="input"
          />
        </Field>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="aplica_iva_fee"
            {...register("aplica_iva_fee")}
            className="size-4"
          />
          <label htmlFor="aplica_iva_fee" className="text-sm">Aplica IVA al fee</label>
        </div>
        {aplicaIva && (
          <Field label="% IVA">
            <input
              type="number"
              step="0.1"
              {...register("porc_iva", { valueAsNumber: true })}
              className="input"
              defaultValue={19}
            />
          </Field>
        )}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="aplica_4x1000"
            {...register("aplica_4x1000")}
            className="size-4"
            defaultChecked
          />
          <label htmlFor="aplica_4x1000" className="text-sm">Aplica 4x1000</label>
        </div>
        {aplica4x1000 && (
          <Field label="% 4x1000">
            <input
              type="number"
              step="0.001"
              {...register("porc_4x1000", { valueAsNumber: true })}
              className="input"
              defaultValue={0.4}
            />
          </Field>
        )}
        <Field label="Días desembolso estimado">
          <input
            type="number"
            {...register("dias_desembolso_estimado", { valueAsNumber: true })}
            className="input"
            defaultValue={3}
          />
        </Field>

        <footer className="flex gap-2 pt-2">
          <button type="submit" disabled={formLoading} className="btn-primary">
            {formLoading ? "Guardando..." : "Guardar"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancelar
          </button>
        </footer>
      </form>

      <ConfirmDialog
        open={pendingData !== null}
        title="¿Cambiar tarifas?"
        description={`Estás cambiando las tarifas. Aplicará a TODAS las liquidaciones desde ahora. Las liquidaciones ya confirmadas no se modifican. (Nuevo fee: ${fee}%)`}
        variant="destructive"
        confirmLabel="Confirmar cambio"
        onConfirm={() => {
          if (pendingData) void onFinish(pendingData);
          setPendingData(null);
        }}
        onCancel={() => setPendingData(null)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}
